import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";

export type PendingSurveyAssignment = {
  assignmentId: string;
  surveyId: string;
  title: string;
  description: string | null;
  surveyType: "impact" | "general";
  waveLabel: string;
  dueAt: string | null;
  assignedAt: string;
};

export function usePendingSurveys() {
  const { user } = useAuth();
  const userId = user?.id;

  const query = useQuery({
    queryKey: ["pending_surveys", userId],
    enabled: !!userId,
    queryFn: async (): Promise<PendingSurveyAssignment[]> => {
      // Fetch all assignments for this user along with the related survey
      // and any responses. We then filter out assignments that already have
      // a completed response.
      const { data, error } = await supabase
        .from("platform_survey_assignments")
        .select(
          `
            id,
            survey_id,
            wave_label,
            due_at,
            assigned_at,
            platform_surveys:survey_id (
              id,
              title,
              description,
              survey_type,
              status
            ),
            platform_survey_responses (
              id,
              completed_at
            )
          `,
        )
        .eq("user_id", userId!)
        .order("assigned_at", { ascending: false });

      if (error) throw error;

      const rows = (data || []).filter((row: any) => {
        const responses: Array<{ completed_at: string | null }> =
          row.platform_survey_responses || [];
        const hasCompleted = responses.some((r) => !!r.completed_at);
        const survey = row.platform_surveys;
        return !hasCompleted && !!survey && survey.status !== "closed";
      });

      return rows.map((row: any) => ({
        assignmentId: row.id,
        surveyId: row.survey_id,
        title: row.platform_surveys.title,
        description: row.platform_surveys.description,
        surveyType: row.platform_surveys.survey_type,
        waveLabel: row.wave_label,
        dueAt: row.due_at,
        assignedAt: row.assigned_at,
      }));
    },
    staleTime: 60 * 1000,
  });

  return {
    pendingAssignments: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
  };
}
